import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import './MikaTable.scss';

export interface MikaColumn {
    key: string;
    title: string;
    render?: (value: any, row: any) => React.ReactNode;
    sortable?: boolean;
    width?: string;
    hideOnMobile?: boolean;
}

export interface MikaAction {
    icon: React.ReactNode;
    tooltip?: string;
    onClick: (row: any) => void;
}

interface MikaTableProps {
    columns: MikaColumn[];
    data: any[];
    actions?: MikaAction[];
    loading?: boolean;
    onRowClick?: (row: any) => void;
    emptyMessage?: string;
    className?: string;
    // Optional controlled-sort: when onSortChange is provided, the table
    // runs in controlled mode — header clicks call onSortChange and the
    // parent is responsible for re-fetching sorted data (e.g. via backend).
    // When omitted, the table sorts the current page client-side as before.
    sortKey?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    onSortChange?: (key: string | null, dir: 'asc' | 'desc' | null) => void;
}

type SortDirection = 'asc' | 'desc' | null;

const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

const MikaTable: React.FC<MikaTableProps> = ({
    columns,
    data,
    actions,
    loading = false,
    onRowClick,
    emptyMessage,
    className = '',
    sortKey: controlledSortKey,
    sortDir: controlledSortDir,
    onSortChange
}) => {
    const intl = useIntl();
    // Default empty-state text must be resolved inside the component so it
    // re-translates on locale change (default-param evaluation captures locale
    // at first render only).
    const resolvedEmptyMessage = emptyMessage ?? intl.formatMessage({
        id: 'common.noResults',
        defaultMessage: 'No results found',
    });

    const isControlled = typeof onSortChange === 'function';
    const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
    const [internalSortDir, setInternalSortDir] = useState<SortDirection>(null);

    const sortKey = isControlled ? (controlledSortKey ?? null) : internalSortKey;
    const sortDir = isControlled ? (controlledSortDir ?? null) : internalSortDir;

    const handleSort = (col: MikaColumn) => {
        if (!col.sortable) return;
        let nextKey: string | null;
        let nextDir: SortDirection;
        if (sortKey === col.key) {
            if (sortDir === 'asc') { nextKey = col.key; nextDir = 'desc'; }
            else { nextKey = null; nextDir = null; }
        } else {
            nextKey = col.key;
            nextDir = 'asc';
        }
        if (isControlled) {
            onSortChange!(nextKey, nextDir);
        } else {
            setInternalSortKey(nextKey);
            setInternalSortDir(nextDir);
        }
    };

    const safeData = Array.isArray(data) ? data : [];

    const sortedData = useMemo(() => {
        // In controlled mode the parent (backend) does the sorting — don't
        // reshuffle the page in JS, just render whatever was passed in.
        if (isControlled) return safeData;
        if (!sortKey || !sortDir) return safeData;
        return [...safeData].sort((a, b) => {
            const aVal = getNestedValue(a, sortKey) ?? '';
            const bVal = getNestedValue(b, sortKey) ?? '';
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [safeData, sortKey, sortDir, isControlled]);

    const skeletonRows = 8;

    return (
        <div className={`mika-table-wrapper ${className}`}>
            <div className="mika-table-scroll">
                <table className="mika-table">
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className={`${col.hideOnMobile ? 'mika-hide-mobile' : ''} ${col.sortable ? 'mika-sortable' : ''}`}
                                    style={col.width ? { width: col.width } : undefined}
                                    onClick={() => handleSort(col)}
                                >
                                    <span className="mika-th-content">
                                        {col.title}
                                        {col.sortable && (
                                            <span className="mika-sort-icon">
                                                {sortKey === col.key
                                                    ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC')
                                                    : ' \u25B4\u25BE'}
                                            </span>
                                        )}
                                    </span>
                                </th>
                            ))}
                            {actions && actions.length > 0 && (
                                <th className="mika-actions-th">
                                    <FormattedMessage id="common.actions" defaultMessage="Actions" />
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: skeletonRows }).map((_, i) => (
                                <tr key={`skel-${i}`} className="mika-skeleton-row">
                                    {columns.map(col => (
                                        <td key={col.key} className={col.hideOnMobile ? 'mika-hide-mobile' : ''}>
                                            <div className="mika-skeleton-cell" />
                                        </td>
                                    ))}
                                    {actions && actions.length > 0 && (
                                        <td><div className="mika-skeleton-cell mika-skeleton-short" /></td>
                                    )}
                                </tr>
                            ))
                        ) : sortedData.length === 0 ? (
                            <tr className="mika-empty-row">
                                <td colSpan={columns.length + (actions ? 1 : 0)}>
                                    <div className="mika-empty-state">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <p>{resolvedEmptyMessage}</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            sortedData.map((row, rowIdx) => (
                                <tr
                                    key={row.id ?? rowIdx}
                                    className={`mika-data-row ${onRowClick ? 'mika-clickable' : ''}`}
                                    onClick={() => onRowClick?.(row)}
                                >
                                    {columns.map(col => (
                                        <td key={col.key} className={col.hideOnMobile ? 'mika-hide-mobile' : ''}>
                                            {col.render
                                                ? col.render(getNestedValue(row, col.key), row)
                                                : getNestedValue(row, col.key) ?? '\u2014'}
                                        </td>
                                    ))}
                                    {actions && actions.length > 0 && (
                                        <td className="mika-actions-td">
                                            {actions.map((action, aIdx) => (
                                                <button
                                                    key={aIdx}
                                                    className="mika-action-btn"
                                                    title={action.tooltip}
                                                    onClick={(e) => { e.stopPropagation(); action.onClick(row); }}
                                                >
                                                    {action.icon}
                                                </button>
                                            ))}
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MikaTable;
